import { match } from 'path-to-regexp'

import { env } from './env'

/**
 * Checks if a given pathname matches a route with parameter placeholders and extracts the parameters.
 * @param pathname - The current pathname (e.g., "/blog/my-post")
 * @param route - The route with placeholders (e.g., "/blog/[slug]")
 * @returns An object with `match` (boolean) and `params` (the extracted parameters if matched, or null if not).
 */
export const matchPathname = (pathname: string, route: string) => {
	const supportedLocales = env.LOCALE_LIST

	const segments = pathname.split('/').filter(Boolean)

	// Remove the first segment if it matches one of the supported locales
	if (supportedLocales.includes(segments[0]!)) {
		segments.shift()
	}

	const pathnameWithoutLocale = '/' + segments.join('/')

	// Normalize the route by ensuring no trailing slash (unless it's root "/")
	const normalizedRoute = route === '/' ? '/' : route.replace(/\/$/, '')

	// Replace Next.js-style `[param]` with path-to-regexp `:param`
	const formattedRoute = normalizedRoute.replace(/\[([^\]]+)]/g, ':$1')

	// Create a matcher using path-to-regexp with `end: true`
	const matcher = match(formattedRoute, {
		decode: decodeURIComponent,
		end: true, // Ensures the match must stop at the end (no extra segments allowed)
	})

	// Match against the pathname without locale segment
	const result = matcher(pathnameWithoutLocale)

	// Ensure exact match: the number of segments must be the same
	const routeSegments = formattedRoute.split('/').filter(Boolean)
	const pathSegments = pathnameWithoutLocale.split('/').filter(Boolean)

	const isExactMatch = !!result && routeSegments.length === pathSegments.length

	return {
		match: isExactMatch,
		params: isExactMatch ? result.params : null,
	}
}
